import { Button, Callout } from '@qr/ui';
import { useRef, useState } from 'react';
import { api, messageFromError } from '../lib/api.js';
import type { PayloadValue } from './PayloadForms.js';

/**
 * Subida del archivo que sirve un codigo: una carta, un catalogo, unas tarifas.
 *
 * El archivo va a Cloudflare R2, no a Firebase Storage. La razon es el coste:
 * Firebase cobra cada megabyte descargado, y este archivo lo descarga cada
 * persona que escanea el codigo; R2 no cobra salida. Para un restaurante con
 * trafico normal es la diferencia entre dolar y medio al mes y cero.
 *
 * El navegador no lleva credenciales de R2. Pide al servidor una autorizacion
 * para subir un archivo concreto (que comprueba sesion, rol y tipo) y sube
 * directamente a Cloudflare con ella. Los bytes no pasan por ninguna funcion:
 * sostener diez megas en memoria mientras alguien sube con mala cobertura es
 * caro y choca con el limite de tamano de peticion.
 *
 * Se guardan la direccion publica y tambien la ruta dentro del bucket. La
 * direccion es a donde lleva el codigo; la ruta es lo que permite borrar el
 * archivo de verdad al reemplazarlo.
 *
 * No hay campo de titulo: quien escanea el codigo cae directamente en el
 * archivo, sin pagina intermedia donde pudiera aparecer.
 */

/** Lo mismo que admite el servidor. Repetirlo aqui es para avisar antes. */
const TIPOS = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const MAXIMO_BYTES = 10 * 1024 * 1024;

function tamanoLegible(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * PUT con barra de progreso.
 *
 * Se usa XMLHttpRequest y no `fetch` porque fetch todavia no informa del
 * progreso de subida en los navegadores, y subir diez megas sin ninguna senal
 * en pantalla parece que se ha colgado.
 */
function subirConProgreso(
  url: string,
  headers: Record<string, string>,
  archivo: File,
  onProgreso: (porcentaje: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const peticion = new XMLHttpRequest();
    peticion.open('PUT', url, true);
    // Estas cabeceras van dentro de la firma: cambiar una invalida la
    // autorizacion y Cloudflare rechaza la subida.
    for (const [nombre, valor] of Object.entries(headers)) {
      peticion.setRequestHeader(nombre, valor);
    }
    peticion.upload.addEventListener('progress', (evento) => {
      if (evento.lengthComputable) {
        onProgreso(Math.round((evento.loaded / evento.total) * 100));
      }
    });
    peticion.addEventListener('load', () => {
      if (peticion.status >= 200 && peticion.status < 300) resolve();
      else reject(new Error(`El almacenamiento respondio ${peticion.status}`));
    });
    peticion.addEventListener('error', () =>
      reject(new Error('No se pudo conectar con el almacenamiento')),
    );
    peticion.addEventListener('abort', () => reject(new Error('Subida cancelada')));
    peticion.send(archivo);
  });
}

export function FileUpload({
  orgId,
  value,
  disabled = false,
  onChange,
}: {
  readonly orgId: string | null;
  readonly value: PayloadValue;
  readonly disabled?: boolean;
  readonly onChange: (value: PayloadValue) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [progreso, setProgreso] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const url = typeof value['url'] === 'string' ? value['url'] : '';
  const fileName = typeof value['fileName'] === 'string' ? value['fileName'] : '';
  const size = typeof value['size'] === 'number' ? value['size'] : 0;
  const contentType =
    typeof value['contentType'] === 'string' ? value['contentType'] : '';
  const rutaAnterior =
    typeof value['storagePath'] === 'string' ? value['storagePath'] : '';

  async function subir(archivo: File) {
    if (!orgId) return;
    setError(null);

    // Se comprueba antes de subir: dejar que el servidor rechace 10 MB despues
    // de haberlos transmitido es tirar los datos moviles de alguien a la basura.
    if (!TIPOS.includes(archivo.type)) {
      setError('Solo se admiten archivos PDF, PNG, JPG o WEBP.');
      return;
    }
    if (archivo.size > MAXIMO_BYTES) {
      setError(
        `El archivo pesa ${tamanoLegible(archivo.size)} y el maximo son ${tamanoLegible(MAXIMO_BYTES)}.`,
      );
      return;
    }

    setProgreso(0);
    try {
      const permiso = await api.createUploadUrl({
        orgId,
        contentType: archivo.type,
        size: archivo.size,
      });

      await subirConProgreso(permiso.uploadUrl, permiso.headers, archivo, setProgreso);

      onChange({
        ...value,
        url: permiso.publicUrl,
        storagePath: permiso.key,
        fileName: archivo.name,
        contentType: archivo.type,
        size: archivo.size,
      });

      // El archivo al que sustituye deja de servir a nadie. Se borra despues de
      // que el nuevo este arriba: si el borrado falla queda un archivo de mas,
      // que es mucho mejor que quedarse sin ninguno.
      //
      // Esto solo cubre reemplazos antes de guardar el codigo; una vez
      // guardado, es el servidor quien retira el anterior al editar o al
      // borrar el codigo.
      if (rutaAnterior && rutaAnterior !== permiso.key) {
        void api
          .deleteUploadedFile({ orgId, key: rutaAnterior })
          .catch((fallo: unknown) => {
            console.error('No se pudo borrar el archivo anterior', fallo);
          });
      }
    } catch (fallo) {
      console.error('No se pudo subir el archivo', fallo);
      setError(messageFromError(fallo));
    } finally {
      setProgreso(null);
    }
  }

  return (
    <div className="payload">
      <div className="qr-field">
        <span className="qr-field__label">Archivo</span>

        {url ? (
          <div className="archivo">
            <div className="archivo__datos">
              <strong>{fileName}</strong>
              <span className="muted small">
                {contentType === 'application/pdf' ? 'PDF' : 'Imagen'} ·{' '}
                {tamanoLegible(size)}
              </span>
            </div>
            <a
              className="archivo__ver"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver
            </a>
            <Button
              size="sm"
              variant="secondary"
              disabled={disabled || progreso !== null}
              onClick={() => input.current?.click()}
            >
              Reemplazar
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            disabled={disabled || progreso !== null}
            onClick={() => input.current?.click()}
          >
            Elegir archivo
          </Button>
        )}

        <input
          ref={input}
          type="file"
          accept={TIPOS.join(',')}
          hidden
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            // Se limpia el input para que elegir el mismo archivo dos veces
            // seguidas vuelva a disparar el evento.
            e.target.value = '';
            if (archivo) void subir(archivo);
          }}
        />

        {progreso !== null ? (
          <div className="archivo__progreso">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-raise">
              <div
                className="h-full rounded-full bg-brand transition-[inline-size] duration-150"
                style={{ inlineSize: `${progreso}%` }}
              />
            </div>
            <span className="muted small tabular-nums">Subiendo... {progreso}%</span>
          </div>
        ) : null}

        <p className="qr-field__hint">
          PDF o imagen, hasta {tamanoLegible(MAXIMO_BYTES)}. Quien escanee el
          codigo lo abre directamente con el visor de su telefono. Al
          reemplazarlo, el codigo impreso sigue funcionando y pasa a mostrar el
          archivo nuevo.
        </p>
      </div>

      {error ? <Callout tone="danger">{error}</Callout> : null}

      {contentType === 'application/pdf' ? (
        <Callout tone="info" title="Sobre los PDF en el móvil">
          Un PDF pensado para papel obliga a ampliar y arrastrar para leerlo en
          una pantalla pequeña, y si pesa mucho tarda en abrirse con datos
          móviles. Si es una carta de restaurante, una imagen por página o una
          versión pensada para móvil se lee bastante mejor.
        </Callout>
      ) : null}
    </div>
  );
}
